(() => {
  if (window.ChicchokMobilePurchaseDrawer) {
    return
  }

  const ROOT_SELECTOR = '[data-chicchok-mobile-purchase-drawer]'
  const MODE_ADD = 'add_to_cart'
  const MODE_BUY = 'buy_now'

  const escapeHtml = value =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  const normalize = value =>
    String(value || '')
      .trim()
      .toLowerCase()

  const isNoCustomValue = value =>
    /^(no(?:\s*\([^)]*\))?|none|no need|not needed|without|default title)$/i.test(
      String(value || '').trim()
    )

  const isDefaultTitleOption = option => {
    const values = option.values || []
    return (
      values.length <= 1 &&
      (normalize(option.name) === 'title' ||
        normalize(values[0]) === 'default title')
    )
  }

  const isSingleNoticeOption = option =>
    (option.values || []).length <= 1 && normalize(option.name).includes('notice')

  const needsOptionalSizeNote = option => {
    const value = normalize((option.values || [])[0])
    return /don't forget|dont forget|note your size|note size/.test(value)
  }

  const shouldShowOption = option => {
    const values = option.values || []
    if (values.length <= 1) {
      return false
    }
    return !isDefaultTitleOption(option)
  }

  class ChicchokMobilePurchaseDrawer {
    constructor(root) {
      this.root = root
      this.formId = root.dataset.productFormId
      this.labels = {
        add: root.dataset.addLabel || 'Add to cart',
        buy: root.dataset.buyLabel || 'Buy now',
        soldOut: root.dataset.soldOutLabel || 'Sold out',
        quantity: root.dataset.quantityLabel || 'Quantity',
      }

      this.data = this.parseData()
      this.mode = MODE_ADD
      this.submitPending = false
      this.submitAttempt = 0
      this.activePurchaseMode = null
      this.checkoutNavigating = false
      this.purchaseCheckoutReady = false
      this.purchaseCheckoutAttempt = undefined
      this.checkoutRecoveryTimer = null
      this.errorMessage = ''
      this.fieldValues = {}
      this.selected = {}
      this.displayOptions = this.data.options.filter(shouldShowOption)
      this.noticeOptions = this.data.options.filter(isSingleNoticeOption)
      this.hasOptionalSizeNote = this.noticeOptions.some(needsOptionalSizeNote)

      this.overlay = root.querySelector('.chicchok-mobile-purchase__overlay')
      this.drawer = root.querySelector('[data-chicchok-mobile-drawer]')
      this.optionsContainer = root.querySelector('[data-chicchok-mobile-options]')
      this.fieldsContainer = root.querySelector('[data-chicchok-mobile-fields]')
      this.status = root.querySelector('[data-chicchok-mobile-status]')
      this.submitButton = root.querySelector('[data-chicchok-mobile-submit]')
      this.quantityInput = root.querySelector('[data-chicchok-mobile-quantity]')
      this.priceNode = root.querySelector('[data-chicchok-mobile-price]')
      this.imageNode = root.querySelector('[data-chicchok-mobile-image]')
      this.actionButtons = Array.from(
        root.querySelectorAll('[data-chicchok-mobile-action]')
      )

      this.currentVariant = this.findInitialVariant()
      this.initializeSelectedOptions()
      this.renderOptions()
      this.bindEvents()
      this.updatePreviewBarOffset()
      this.updateState()
    }

    parseData() {
      const script = this.root.querySelector('[data-product-options-json]')
      if (!script) {
        return { product: {}, options: [], variants: [] }
      }

      try {
        return JSON.parse(script.textContent || '{}')
      } catch (error) {
        console.warn('CHICCHOK mobile drawer product JSON failed to parse', error)
        return { product: {}, options: [], variants: [] }
      }
    }

    bindEvents() {
      this.actionButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.disabled || this.submitPending) {
            return
          }
          this.open(button.dataset.chicchokMobileAction || MODE_ADD)
        })
      })

      this.root.querySelectorAll('[data-chicchok-mobile-close]').forEach(node => {
        node.addEventListener('click', () => this.close())
      })

      this.root
        .querySelector('[data-chicchok-quantity-minus]')
        ?.addEventListener('click', () => this.stepQuantity(-1))
      this.root
        .querySelector('[data-chicchok-quantity-plus]')
        ?.addEventListener('click', () => this.stepQuantity(1))

      this.quantityInput?.addEventListener('change', () => {
        this.quantityInput.value = String(this.getQuantity())
      })

      this.optionsContainer.addEventListener('click', event => {
        const button = event.target.closest('[data-option-index][data-option-value]')
        if (!button) {
          return
        }

        const index = Number(button.dataset.optionIndex)
        this.selected[index] = button.dataset.optionValue
        this.renderOptions()
        this.updateState()
      })

      this.optionsContainer.addEventListener('change', event => {
        const select = event.target.closest('[data-option-index]')
        if (!select) {
          return
        }

        this.selected[Number(select.dataset.optionIndex)] = select.value
        this.updateState()
      })

      this.fieldsContainer.addEventListener('input', event => {
        const field = event.target.closest('[data-property-key]')
        if (!field) {
          return
        }
        this.fieldValues[field.dataset.propertyKey] = field.value
      })

      this.submitButton?.addEventListener('click', () => {
        if (this.submitPending) return
        this.submit(this.mode)
      })

      document.addEventListener('variant:changed', event => {
        const form = event.target
        if (!form || form.id !== this.formId || !event.detail?.variant) {
          return
        }
        const variant = this.data.variants.find(
          item => String(item.id) === String(event.detail.variant.id)
        )
        if (variant) {
          this.currentVariant = variant
          variant.options.forEach((value, index) => {
            this.selected[index] = value
          })
          this.updateState()
        }
      })

      document.addEventListener('chicchok:checkout-state', () => this.updateState())

      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.isOpen()) {
          this.close()
        }
      })

      window.addEventListener('resize', () => this.updatePreviewBarOffset())
      window.addEventListener('pagehide', () => {
        if (this.checkoutNavigating || this.purchaseCheckoutReady || (this.submitPending && this.activePurchaseMode === MODE_BUY)) this.resetCheckoutSubmission()
      })
      window.addEventListener('pageshow', event => {
        if (event.persisted && (this.checkoutNavigating || this.purchaseCheckoutReady || (this.submitPending && this.activePurchaseMode === MODE_BUY))) this.resetCheckoutSubmission()
      })

      if (window.MutationObserver) {
        const previewObserver = new MutationObserver(() =>
          this.updatePreviewBarOffset()
        )
        previewObserver.observe(document.documentElement, {
          childList: true,
          subtree: true,
        })
      }

      window.setTimeout(() => this.updatePreviewBarOffset(), 1000)
      window.setTimeout(() => this.updatePreviewBarOffset(), 3000)
    }

    updatePreviewBarOffset() {
      const wrapper =
        document.getElementById('PBarNextFrameWrapper') ||
        document.getElementById('PBarNextFrame')?.parentElement
      const rect = wrapper?.getBoundingClientRect()
      const height =
        rect && rect.height > 0 && rect.bottom > window.innerHeight - 8
          ? Math.ceil(rect.height)
          : 0

      document.documentElement.style.setProperty(
        '--chicchok-mobile-purchase-preview-offset',
        `${height}px`
      )
    }

    findInitialVariant() {
      const selectedId = this.data.product?.selected_variant_id
      return (
        this.data.variants.find(
          variant => String(variant.id) === String(selectedId)
        ) ||
        this.data.variants.find(variant => variant.available) ||
        this.data.variants[0] ||
        null
      )
    }

    initializeSelectedOptions() {
      this.data.options.forEach((option, index) => {
        this.selected[index] =
          this.currentVariant?.options?.[index] ||
          option.selected ||
          (option.values || [])[0] ||
          ''
      })
    }

    renderOptions() {
      const noticeHtml = this.noticeOptions
        .filter(option => !isDefaultTitleOption(option))
        .map(option => {
          const value = (option.values || [])[0]
          if (!value) {
            return ''
          }
          return `<div class="chicchok-mobile-purchase__notice">${escapeHtml(
            value
          )}</div>`
        })
        .join('')

      const optionHtml = this.displayOptions
        .map(option => this.renderOption(option))
        .join('')

      this.optionsContainer.innerHTML = noticeHtml + optionHtml
      this.renderFields()
    }

    renderOption(option) {
      const index = Number(option.position || 1) - 1
      const values = option.values || []
      const selectedValue = this.selected[index] || values[0] || ''

      if (values.length > 8) {
        const optionsHtml = values
          .map(value => {
            const selected = value === selectedValue ? ' selected' : ''
            return `<option value="${escapeHtml(value)}"${selected}>${escapeHtml(
              value
            )}</option>`
          })
          .join('')

        return `
          <div class="chicchok-mobile-purchase__option">
            <div class="chicchok-mobile-purchase__option-title">
              <span>${escapeHtml(option.name)}</span>
            </div>
            <select class="chicchok-mobile-purchase__select" data-option-index="${index}">
              ${optionsHtml}
            </select>
          </div>
        `
      }

      const buttonsHtml = values
        .map(value => {
          const pressed = value === selectedValue ? 'true' : 'false'
          return `
            <button
              type="button"
              class="chicchok-mobile-purchase__choice"
              data-option-index="${index}"
              data-option-value="${escapeHtml(value)}"
              aria-pressed="${pressed}"
            >
              ${escapeHtml(value)}
            </button>
          `
        })
        .join('')

      return `
        <div class="chicchok-mobile-purchase__option">
          <div class="chicchok-mobile-purchase__option-title">
            <span>${escapeHtml(option.name)}</span>
            <span>${escapeHtml(selectedValue)}</span>
          </div>
          <div class="chicchok-mobile-purchase__choice-grid">
            ${buttonsHtml}
          </div>
        </div>
      `
    }

    renderFields() {
      const requirements = this.getPropertyRequirements()
      this.fieldsContainer.innerHTML = requirements
        .map(item => {
          const requiredMark = item.required
            ? ' <span class="chicchok-mobile-purchase__required">*</span>'
            : ''
          const value = this.fieldValues[item.key] || ''
          return `
            <div class="chicchok-mobile-purchase__field">
              <label for="${item.id}">${escapeHtml(item.key)}${requiredMark}</label>
              <textarea
                id="${item.id}"
                class="chicchok-mobile-purchase__input"
                data-property-key="${escapeHtml(item.key)}"
                ${item.required ? 'required' : ''}
                placeholder="${escapeHtml(item.placeholder)}"
              >${escapeHtml(value)}</textarea>
            </div>
          `
        })
        .join('')
    }

    getPropertyRequirements() {
      const entries = this.data.options.map((option, index) => ({
        name: option.name || '',
        value: this.selected[index] || '',
      }))
      const selectedText = entries
        .map(entry => `${entry.name} ${entry.value}`)
        .join(' | ')

      const requirements = []
      if (/other colors?|favorite color/i.test(selectedText)) {
        requirements.push({
          key: 'Preferred color',
          required: true,
          placeholder: 'Tell us your preferred color.',
          id: 'chicchok-preferred-color',
        })
      }

      if (/custom sizing|body measurements|custom size|measurements/i.test(selectedText)) {
        requirements.push({
          key: 'Body measurements / size note',
          required: true,
          placeholder: 'Add neck/body measurements or sizing notes.',
          id: 'chicchok-size-note',
        })
      } else if (this.hasOptionalSizeNote) {
        requirements.push({
          key: 'Body measurements / size note',
          required: false,
          placeholder: 'Recommended: note your size.',
          id: 'chicchok-size-note',
        })
      }

      const needsCustomName = entries.some(entry => {
        const combined = `${entry.name} ${entry.value}`
        return (
          /laser engraving|custom letters?|custom name|engraving/i.test(combined) &&
          !isNoCustomValue(entry.value)
        )
      })

      if (needsCustomName) {
        requirements.push({
          key: 'Custom name / engraving',
          required: true,
          placeholder: 'Add the custom name, letters, or engraving text.',
          id: 'chicchok-custom-name',
        })
      }

      return requirements
    }

    getSelectedOptions() {
      return this.data.options.map((option, index) => {
        return this.selected[index] || (option.values || [])[0] || ''
      })
    }

    findCurrentVariant() {
      const selectedOptions = this.getSelectedOptions()
      return (
        this.data.variants.find(variant => {
          return selectedOptions.every((value, index) => {
            return String(variant.options?.[index] || '') === String(value)
          })
        }) || null
      )
    }

    updateState() {
      this.currentVariant = this.findCurrentVariant()
      const available = Boolean(this.currentVariant?.available)
      const disabledLabel = this.labels.soldOut
      const blocked = this.submitPending || Boolean(window.CHICCHOK_CART_FLOW?.isCheckoutLocked())

      this.actionButtons.forEach(button => {
        button.disabled = !available || blocked
        if (this.submitPending) button.setAttribute('aria-busy', 'true')
        else button.removeAttribute('aria-busy')
      })

      if (this.submitButton) {
        this.submitButton.disabled = !available || blocked
        if (this.submitPending) this.submitButton.setAttribute('aria-busy', 'true')
        else this.submitButton.removeAttribute('aria-busy')
        this.submitButton.textContent = available
          ? this.mode === MODE_BUY
            ? this.labels.buy
            : this.labels.add
          : disabledLabel
      }

      if (this.status) {
        this.status.textContent = available
          ? this.errorMessage
          : 'This option combination is currently unavailable.'
      }

      if (this.priceNode && this.currentVariant) {
        this.priceNode.textContent = this.formatMoney(this.currentVariant.price)
      }

      if (this.imageNode && this.currentVariant?.image) {
        this.imageNode.src = this.currentVariant.image
      }
    }

    shouldOpenDrawer() {
      return (
        this.displayOptions.length > 0 ||
        this.noticeOptions.length > 0 ||
        this.hasOptionalSizeNote
      )
    }

    open(mode) {
      if (this.submitPending) return
      this.mode = mode === MODE_BUY ? MODE_BUY : MODE_ADD
      this.updateState()

      if (!this.shouldOpenDrawer()) {
        this.submit(this.mode)
        return
      }

      this.drawer.dataset.mode = this.mode
      this.overlay.hidden = false
      this.drawer.hidden = false
      document.documentElement.classList.add('chicchok-mobile-purchase-open')
      document.body.style.overflow = 'hidden'
      this.submitButton?.focus()
    }

    close() {
      this.overlay.hidden = true
      this.drawer.hidden = true
      document.documentElement.classList.remove('chicchok-mobile-purchase-open')
      document.body.style.overflow = ''
    }

    isOpen() {
      return this.drawer && !this.drawer.hidden
    }

    stepQuantity(step) {
      if (!this.quantityInput) {
        return
      }
      this.quantityInput.value = String(Math.max(1, this.getQuantity() + step))
    }

    getQuantity() {
      const raw = parseInt(this.quantityInput?.value || '1', 10)
      return Number.isFinite(raw) && raw > 0 ? raw : 1
    }

    getProperties() {
      const properties = {}
      this.fieldsContainer
        .querySelectorAll('[data-property-key]')
        .forEach(field => {
          const key = field.dataset.propertyKey
          const value = String(field.value || '').trim()
          if (key && value) {
            properties[key] = value
          }
        })
      return properties
    }

    validateProperties() {
      const missingField = Array.from(
        this.fieldsContainer.querySelectorAll('[data-property-key][required]')
      ).find(field => !String(field.value || '').trim())

      if (missingField) {
        const key = missingField.dataset.propertyKey || 'required field'
        this.status.textContent = `Please fill in ${key}.`
        missingField.focus()
        return false
      }

      return true
    }

    syncOriginalForm(payload) {
      const form = document.getElementById(this.formId)
      if (!form || !payload) {
        return null
      }

      const idInput = form.querySelector('[name="id"]')
      if (idInput) {
        idInput.disabled = false
        idInput.value = String(payload.id)
      }

      const quantityInput = form.querySelector('[name="quantity"]')
      if (quantityInput) {
        quantityInput.value = String(payload.quantity)
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      form
        .querySelectorAll('[data-chicchok-mobile-property]')
        .forEach(input => input.remove())

      Object.entries(payload.properties || {}).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = `properties[${key}]`
        input.value = value
        input.dataset.chicchokMobileProperty = 'true'
        form.appendChild(input)
      })

      return form
    }

    async submit(mode) {
      if (this.submitPending) return
      this.errorMessage = ''
      this.updateState()

      if (!this.currentVariant?.available) {
        this.status.textContent = 'This option combination is currently unavailable.'
        return
      }

      this.renderFields()
      if (!this.validateProperties()) {
        return
      }

      // Capture the shopper's choice before queueing: options may change while
      // another cart mutation or this request is still in flight.
      const payload = {
        id: Number(this.currentVariant.id),
        quantity: this.getQuantity(),
        sections: ['mini-cart'],
        sections_url: window.location.pathname,
      }
      const properties = this.getProperties()
      if (Object.keys(properties).length > 0) {
        payload.properties = properties
      }

      const purchaseMode = mode === MODE_BUY ? MODE_BUY : MODE_ADD
      const attempt = ++this.submitAttempt
      this.activePurchaseMode = purchaseMode
      this.submitPending = true
      this.updateState()

      let addSucceeded = false
      try {
        this.syncOriginalForm(payload)
        const flow = window.CHICCHOK_CART_FLOW
        let cartRevision
        const performAdd = async revision => {
          cartRevision = revision
          return this.addWithFetch(payload)
        }
        const responseJson = flow
          ? await flow.enqueueMutation(performAdd)
          : await performAdd()
        addSucceeded = true
        // Analytics and cart rendering must not wait inside the mutation queue.
        await this.handleAddSuccess(purchaseMode, payload, responseJson, cartRevision, attempt)
      } catch (error) {
        // A pagehide/BFCache recovery may have started a newer purchase. An old
        // request must not unlock it, show a stale error, or resume checkout.
        if (attempt !== this.submitAttempt) return
        if (this.checkoutNavigating || this.purchaseCheckoutReady) this.resetCheckoutSubmission()
        const message = error?.message || this.getCartString(
          'cartOperationError',
          'Unable to add this item. Please check your cart before trying again.'
        )
        this.errorMessage = message
        if (this.status) this.status.textContent = message
        document.documentElement.dispatchEvent(
          new CustomEvent('cart-notification:show', {
            bubbles: true,
            cancelable: true,
            detail: { status: 'error', error: message },
          })
        )
        if (!addSucceeded) {
          // An aborted/failed response does not prove Shopify did not apply the
          // add. Reconcile the cart, but never automatically resend the add.
          document.documentElement.dispatchEvent(new CustomEvent('chicchok:cart-resync', { bubbles: true }))
        }
      } finally {
        // A successful BuyNow stays locked until navigation actually leaves
        // this page, rather than allowing another add in the navigation gap.
        if (attempt === this.submitAttempt) {
          if (!this.checkoutNavigating) {
            this.submitPending = false
            this.activePurchaseMode = null
          }
          this.updateState()
        }
      }
    }

    getCartString(key, fallback) {
      return window.themeVariables?.strings?.[key] || fallback
    }

    async addWithFetch(payload) {
      const routes = window.themeVariables?.routes || {}
      const cartAddUrl = routes.cartAddUrl || '/cart/add'
      const controller = new AbortController()
      let timedOut = false
      const timer = window.setTimeout(() => {
        timedOut = true
        controller.abort()
      }, 10000)

      try {
        const response = await fetch(`${cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        })
        const responseJson = await response.json()

        if (!response.ok) {
          throw new Error(responseJson.description || this.getCartString(
            'cartOperationError',
            'Unable to add this item. Please check your cart before trying again.'
          ))
        }
        return responseJson
      } catch (error) {
        if (timedOut) {
          throw new Error(this.getCartString(
            'cartOperationTimeout',
            'The cart request timed out. Please check your cart before trying again.'
          ))
        }
        throw error
      } finally {
        window.clearTimeout(timer)
      }
    }

    async handleAddSuccess(mode, payload, responseJson, cartRevision, attempt) {
      if (attempt !== this.submitAttempt) return
      if (mode === MODE_BUY && window.CHICCHOK_CART_FLOW?.preparePurchaseCheckout) {
        // The add has settled: wait for notes/protection and confirm the fresh
        // cart outside the mutation queue before handing off to checkout.
        const flow = window.CHICCHOK_CART_FLOW
        const owned = !flow.isCheckoutLocked()
        const preparation = flow.preparePurchaseCheckout({ isCurrent: () => attempt === this.submitAttempt })
        this.purchaseCheckoutReady = owned && flow.isCheckoutLocked()
        this.purchaseCheckoutAttempt = this.purchaseCheckoutReady ? flow.getCheckoutAttempt?.() : undefined
        await preparation
        if (attempt !== this.submitAttempt) return
      }

      // Track only a successful add, using the returned variant and quantity.
      // Keep this separate from theme UI events that can open/close drawers.
      try {
        const tracking = window.CHICCHOK_OPENAI_TRACKING
        if (tracking) {
          for (const item of responseJson.items || [responseJson]) {
            // Shopify may return the total existing line quantity after adding.
            if (String(item.variant_id || item.id) === String(payload.id)) {
              tracking.added({ ...item, quantity: payload.quantity })
            }
          }
          if (mode === MODE_BUY) await tracking.prepareCheckout()
        }
      } catch (error) {
        // An analytics error must not fail an otherwise successful purchase flow.
      }

      if (attempt !== this.submitAttempt) return

      if (mode === MODE_BUY) {
        this.checkoutNavigating = true
        this.armCheckoutRecovery()
        window.location.href = '/checkout'
        return
      }

      this.close()
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:show-and-fetch', {
          bubbles: true,
          detail: {
            drawerEl: null,
            sections: responseJson.sections,
            cartRevision,
          },
        })
      )
      document.documentElement.dispatchEvent(
        new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'success' },
        })
      )
    }

    resetCheckoutSubmission() {
      this.submitAttempt += 1
      window.clearTimeout(this.checkoutRecoveryTimer)
      this.checkoutRecoveryTimer = null
      if (this.purchaseCheckoutReady) window.CHICCHOK_CART_FLOW?.endCheckout(this.purchaseCheckoutAttempt)
      this.purchaseCheckoutReady = false
      this.purchaseCheckoutAttempt = undefined
      this.checkoutNavigating = false
      this.submitPending = false
      this.activePurchaseMode = null
      this.updateState()
    }

    armCheckoutRecovery() {
      window.clearTimeout(this.checkoutRecoveryTimer)
      this.checkoutRecoveryTimer = window.setTimeout(() => {
        if (!this.checkoutNavigating) return
        this.errorMessage = this.getCartString(
          'cartOperationError',
          'Checkout could not open. Please check your cart before trying again.'
        )
        this.resetCheckoutSubmission()
        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: this.errorMessage },
        }))
      }, 20000)
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney && this.data.product?.money_format) {
        return window.Shopify.formatMoney(cents, this.data.product.money_format)
      }

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.data.product?.currency || 'USD',
        }).format(Number(cents || 0) / 100)
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`
      }
    }
  }

  window.ChicchokMobilePurchaseDrawer = ChicchokMobilePurchaseDrawer

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (root.__chicchokMobilePurchaseDrawer) {
        return
      }
      root.__chicchokMobilePurchaseDrawer = new ChicchokMobilePurchaseDrawer(root)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
